/**
 * productUtils.js - helpers for checkout product handling.
 */

/**
 * Flatten the batch /subscriptions/products-by-channels response into a plain
 * product list. The API nests products per requested channel:
 *   { success, products: [{ channel_id, products: [...] }, ...] }
 * Consumers (CheckoutPage) expect flat product objects, tagged with the
 * channel they belong to so season vs. full-series products can be labeled.
 *
 * @param {Object|null} response - raw batch endpoint response
 * @param {Object} ctx
 * @param {string} [ctx.currentChannelId] - dspro id of the channel being bought
 * @param {string} [ctx.currentTitle] - display title for current-channel products
 * @param {string} [ctx.parentChannelId] - dspro id of the parent series, if any
 * @param {string} [ctx.parentTitle] - display title for parent products
 * @returns {Array} flat product list
 */
export function flattenBatchProducts(response, { currentChannelId, currentTitle, parentChannelId, parentTitle } = {}) {
  const entries = response?.products;
  if (!Array.isArray(entries)) return [];

  return entries.flatMap(entry => {
    // Defensive: some responses may already be flat product objects
    if (!Array.isArray(entry?.products)) return entry && entry._id ? [entry] : [];

    const isParent = !!parentChannelId && entry.channel_id === parentChannelId;
    return entry.products.map(product => ({
      ...product,
      _sourceChannelId: entry.channel_id,
      _sourceChannelTitle: isParent ? parentTitle || 'Complete Series' : currentTitle,
      _isParentProduct: isParent,
    }));
  });
}
